import { gql } from '@apollo/client';
import { ACFMediaFragment } from '@/fragments';
import { InducteeArtifactSingularFragment, ArtifactSingularFragment } from '@graphqlTypes';
import { Container, Button, VideoEmbed, RevealWrapper, Image } from '@components';
import { getLinkData, getVideoEmbed } from '@/core/utils';
import { useRef } from 'react';
import { useViewSize } from '@/providers/ViewSizeContext';
import { useClientMount } from '@/providers/ClientMountProvider';
import { useInView } from 'motion/react';
import { AnimVals } from '@/utils/animUtils';

import styles from './ArtifactSingular.module.scss';
import className from 'classnames/bind';
import ArtifactImageRight from './Variants/ImageRight';
const cx = className.bind(styles);

export type ArtifactSingularProps = InducteeArtifactSingularFragment | ArtifactSingularFragment;

export default function ArtifactSingular(props: ArtifactSingularProps & { className?: string }) {
  const {
    layout,
    eyebrow,
    artifactSingularHeadline,
    artifactSingularBody,
    image,
    videoEmbed,
    cta,
    className,
  } = props;

  // Note: This is a temporary override for developing layouts
  // In the future, this should be replaced with the commented line below
  //    const layoutValue: 'image-right' | 'image-bottom' | 'image-diagonal' = 'image-diagonal'; // Temporary override
  //    const layoutValue = layout as 'image-right' | 'image-bottom' | 'image-diagonal'; // TODO: Use this later -Gabe

  const [componentLayout] = layout;
  const ctaData = getLinkData(cta);
  const showVideoEmbed = getVideoEmbed(videoEmbed) !== null;

  // -------------------- Determine markup variant

  function getVariantMarkup() {
    switch (componentLayout) {

      // -------------------- Image diagonal variant (default) - Do not change

      case 'image-diagonal':
      default: {
        // Original markup for diagonal variant (no breakpoint conditions needed) - Do not change
        return (
          <>
            {/* -------------------- Title */}
            <div className={cx("title")}>
              { eyebrow && <p className={cx(["eyebrow-l", "eyebrow"])}>{eyebrow}</p> }
              <h2 className={cx("title-uc-l", "title__lines")}>
                {artifactSingularHeadline.map((line, index) => (
                  <span
                    key={index}
                    className={cx(['line', `title__lines__line-${index}`])}
                    dangerouslySetInnerHTML={{ __html: line.line }}
                  />
                ))}
              </h2>
            </div>
            {/* -------------------- Artifact */}
            <div className={cx("artifact__artifact")}>
              <Image className={cx("image")} image={image} />
            </div>
            {/* -------------------- YouTube */}
            {showVideoEmbed && (
              <div className={cx("video")}>
                <VideoEmbed {...videoEmbed} consentSmall={true} />
              </div>
            )}
            {/* -------------------- Body */}
            <div className={cx("body")}>
                {artifactSingularBody.map((line, index) => {
                  if (index === artifactSingularBody.length - 1) {
                    return (
                      <div className={cx("body__p-container")} key={index}>
                        <p className={"text-m"} dangerouslySetInnerHTML={{ __html: line.text }} />
                        {ctaData && (
                          <Button {...ctaData} />
                        )}
                      </div>
                    );
                  }
                  return (
                    <div className={cx("body__p-container")} key={index}>
                      <p className={"text-m"} dangerouslySetInnerHTML={{ __html: line.text }} />
                    </div>
                  );
                })}
            </div>
          </>
        );
      }

      // -------------------- Image right/bottom variants

      case 'image-right':
      case 'image-left': {
        return <ArtifactImageRight {...props} />;
      }
      case 'image-bottom': {
        // Note: The 'artifact' and the 'video' sections are
        // nested in a new parent named 'artifact__imagery'

        // All breakpoints are satisfied by the same markup
        return (
          <>
            {/* Title */}
            <div className={cx("title")}>
              { eyebrow && <p className={cx(["eyebrow-l", "eyebrow"])}>{eyebrow}</p> }
              <h2 className={cx("title-uc-l", "title__lines")}>
                {artifactSingularHeadline.map((line, index) => (
                  <span
                    key={index}
                    className={cx(['line', `title__lines__line-${index}`])}
                    dangerouslySetInnerHTML={{ __html: line.line }}
                  />
                ))}
              </h2>
            </div>

            {/* Artifact + YouTube combined` */}
            <div className={cx("artifact__imagery")}>
              {/* Artifact */}
              <div className={cx("artifact__artifact")}>
                <Image className={cx("image")} image={image} />
              </div>
              {/* YouTube */}
              {showVideoEmbed && (
                <div className={cx("video")}>
                  <VideoEmbed {...videoEmbed} consentSmall={true} />
                </div>
              )}
            </div>

            {/* Body */}
            <div className={cx("body")}>
              {artifactSingularBody.map((line, index) => (
                <p className={"text-m"} key={index} dangerouslySetInnerHTML={{ __html: line.text }} />
              ))}
            </div>
          </>
        );
      }

      // -------------------- Image bottom variant

      // Note: This variant does not currently require any special markup
      // but is included here for future reference if needed -Gabe

      /* case 'image-bottom': {
        return (
          <>
            IMAGE BOTTOM
          </>
        );
      } */

    }
  }

  // -------------------- Animation

  const rootEl = useRef<HTMLElement>(null);
  const { isDesktop, isReady } = useViewSize();
  const { mounted } = useClientMount();
  const REVEAL = AnimVals;
  // Only use isDesktop when isReady to avoid SSR/client mismatch
  const triggerPercent = (mounted && isReady && isDesktop) ? REVEAL.lg.triggerPercent : REVEAL.sm.triggerPercent;
  const isInView = useInView(rootEl, { amount: triggerPercent, once: REVEAL.once });

  // -------------------- Render

  return (
    <section className={cx(["artifact-singular", `variant-${layout}`, className])} ref={rootEl}>
      <RevealWrapper isInView={isInView}>
        <Container className={cx("content")}>
          {getVariantMarkup()}
        </Container>
      </RevealWrapper>
    </section>
  );

}

ArtifactSingular.fragments = {
  entry: gql`
    fragment ArtifactSingular on FlexiblePageContentContentArtifactSingularLayout {
      layout
      eyebrow
      artifactSingularHeadline: headlineLines {
        line
      }
      artifactSingularBody: body {
        text
      }
      image {
        ...ACFMedia
      }
      cta {
        linkType
        label
        sectionId
        externalLink
        page {
          node {
            uri
          }
        }
      }
      videoEmbed {
        videoType
        youtubeVideoId
        videoUrl
        videoThumbnail {
          ...ACFMedia
        }
        youtubeThumbnail {
          ...ACFMedia
        }
      }
    }
  `,
  inducteeEntry: gql`
    ${ACFMediaFragment}
    fragment InducteeArtifactSingular on InducteeDataImpactFlexibleContentArtifactSingularLayout {
      layout
      eyebrow
      artifactSingularHeadline: headlineLines {
        line
      }
      artifactSingularBody: body {
        text
      }
      image {
        ...ACFMedia
      }
      cta {
        linkType
        label
        sectionId
        externalLink
        page {
          node {
            uri
          }
        }
      }
      videoEmbed {
        videoType
        youtubeVideoId
        videoUrl
        videoThumbnail {
          ...ACFMedia
        }
        youtubeThumbnail {
          ...ACFMedia
        }
      }
    }
  `,
};
